// RamsTemplatesPage — list + create + duplicate + AI-import RAMS questionnaire templates
import { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, X, Save, Copy, Archive, Sparkles, ListChecks } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Pill from "@/components/Pill";

const STATUS_TONE = { draft: "amber", published: "green", archived: "neutral" };
const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant"]);

export default function RamsTemplatesPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(false);
    const [importDialog, setImportDialog] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/rams/templates");
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load RAMS templates");
        }
        setLoading(false);
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const duplicate = async (id) => {
        try {
            const { data } = await api.post(`/rams/templates/${id}/duplicate`);
            toast.success("Duplicated — opening the copy");
            refresh();
            navigate(`/app/rams/templates/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Duplicate failed");
        }
    };

    const archive = async (id) => {
        if (!window.confirm("Archive this template? It will no longer be available to issue.")) return;
        try {
            await api.delete(`/rams/templates/${id}`);
            toast.success("Template archived");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not archive — it may still be in use");
        }
    };

    return (
        <div data-testid="rams-templates-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                        style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>
                        RAMS
                    </span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">RAMS Questionnaire Templates</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {rows.length} template{rows.length !== 1 ? "s" : ""} · {rows.filter((t) => t.status === "published").length} published
                    </p>
                </div>
                {canEdit && (
                    <div className="flex items-center gap-2">
                        <button data-testid="rams-template-ai-import-btn" onClick={() => setImportDialog(true)}
                            className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white bg-ai-grad">
                            <Sparkles size={15} /> Import with Beyond AI
                        </button>
                        <button data-testid="rams-template-new-btn" onClick={() => setDialog(true)}
                            className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white"
                            style={{ background: "var(--brand-accent, #C75A1B)" }}>
                            <Plus size={15} /> New template
                        </button>
                    </div>
                )}
            </div>

            <div className="rounded-2xl bg-white border border-[#E5E0DA] overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[#F5F3F0] text-left text-[11px] uppercase tracking-wider text-[#6F6A64] border-b border-[#E5E0DA]">
                        <tr>
                            <th className="px-4 py-3 font-semibold">Template</th>
                            <th className="px-4 py-3 font-semibold">Work type</th>
                            <th className="px-4 py-3 font-semibold">Version</th>
                            <th className="px-4 py-3 font-semibold">Status</th>
                            <th className="px-4 py-3 font-semibold w-40"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="text-center py-10 text-[#9A968F]">
                                <Loader2 size={16} className="inline animate-spin mr-2" />Loading templates…
                            </td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={5} className="text-center py-10 text-[#9A968F]">No RAMS templates yet.</td></tr>
                        ) : rows.map((t) => (
                            <tr key={t.id} data-testid={`rams-template-row-${t.id}`}
                                className="border-b border-[#F0EDE8] hover:bg-[#F5F3F0] cursor-pointer"
                                onClick={() => navigate(`/app/rams/templates/${t.id}`)}>
                                <td className="px-4 py-3">
                                    <div className="font-semibold text-[#1F1F1F]">{t.name}</div>
                                    {t.description && <div className="text-[12px] text-[#6F6A64] truncate max-w-md">{t.description}</div>}
                                </td>
                                <td className="px-4 py-3 text-[#6F6A64]">{t.work_type || "—"}</td>
                                <td className="px-4 py-3 text-[#6F6A64]">
                                    v{t.published_version_number || t.current_version_number}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-1.5">
                                        <Pill tone={STATUS_TONE[t.status] || "neutral"}>{t.status}</Pill>
                                        {t.status === "published" && t.published_version_number &&
                                            t.current_version_number > t.published_version_number && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-[#FCF1DC] text-[#C68A1C]">
                                                draft v{t.current_version_number} in progress
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-3">
                                        {canEdit && (
                                            <>
                                                <button data-testid={`rams-template-duplicate-${t.id}`}
                                                    onClick={(e) => { e.stopPropagation(); duplicate(t.id); }}
                                                    className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#6F6A64] hover:text-[#1F1F1F] hover:underline">
                                                    <Copy size={12} /> Duplicate
                                                </button>
                                                <button data-testid={`rams-template-archive-${t.id}`}
                                                    onClick={(e) => { e.stopPropagation(); archive(t.id); }}
                                                    className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#9A968F] hover:text-red-600 hover:underline">
                                                    <Archive size={12} /> Archive
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {dialog && <NewTemplateDialog onClose={() => setDialog(false)}
                onCreated={(t) => { setDialog(false); refresh(); navigate(`/app/rams/templates/${t.id}`); }} />}
            {importDialog && <ImportDialog onClose={() => setImportDialog(false)}
                onCreated={(t) => { setImportDialog(false); refresh(); navigate(`/app/rams/templates/${t.id}`); }} />}
        </div>
    );
}

function NewTemplateDialog({ onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", work_type: "", description: "" });
    const [saving, setSaving] = useState(false);

    async function submit() {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/rams/templates", form);
            toast.success("Template created — start adding sections");
            onCreated(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not create template");
        }
        setSaving(false);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="new-rams-template-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#1F1F1F]">New RAMS questionnaire</h3>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <Field label="Name *">
                        <input data-testid="new-rams-template-name" value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="e.g. Scaffolding RAMS Questionnaire" className={inputCls} />
                    </Field>
                    <Field label="Work type">
                        <input data-testid="new-rams-template-work-type" value={form.work_type}
                            onChange={(e) => setForm({ ...form, work_type: e.target.value })}
                            placeholder="e.g. Scaffolding" className={inputCls} />
                    </Field>
                    <Field label="Description">
                        <textarea rows={3} value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className={inputCls + " resize-none"} />
                    </Field>
                </div>
                <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-end gap-2 bg-[#F5F3F0]">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">Cancel</button>
                    <button data-testid="new-rams-template-save" onClick={submit} disabled={saving}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Create
                    </button>
                </div>
            </div>
        </div>
    );
}

function ImportDialog({ onClose, onCreated }) {
    const [file, setFile] = useState(null);
    const [text, setText] = useState("");
    const [importing, setImporting] = useState(false);

    async function submit() {
        if (!file && !text.trim()) { toast.error("Upload a document or paste text"); return; }
        setImporting(true);
        try {
            const fd = new FormData();
            if (file) fd.append("file", file);
            if (text.trim()) fd.append("text_input", text);
            const { data } = await api.post("/rams/templates/ai-import", fd, {
                headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
            });
            toast.success("Draft questionnaire created — review before publishing");
            onCreated(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) ||
                "Import is taking longer than expected — check the templates list, it may have completed in the background.");
        }
        setImporting(false);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="rams-import-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#1F1F1F] flex items-center gap-2"><Sparkles size={16} /> Import with Beyond AI</h3>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <p className="text-[12px] text-[#6F6A64]">
                        Upload an existing RAMS questionnaire or method statement (Word/PDF/text) and Beyond AI
                        will draft a reusable, client-agnostic questionnaire template from it — always review before publishing.
                    </p>
                    <Field label="Document">
                        <input data-testid="rams-import-file" type="file" accept=".docx,.pdf,.txt"
                            onChange={(e) => setFile(e.target.files?.[0] || null)} className={inputCls + " pt-1.5"} />
                    </Field>
                    <Field label="Or paste text">
                        <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)}
                            placeholder="Paste questionnaire text here instead of uploading a file"
                            className={inputCls + " resize-none"} />
                    </Field>
                    {importing && (
                        <p className="text-[12px] text-[#9A968F] italic">Analysing document — this can take up to a minute…</p>
                    )}
                </div>
                <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-end gap-2 bg-[#F5F3F0]">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">Cancel</button>
                    <button data-testid="rams-import-submit" onClick={submit} disabled={importing}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60 bg-ai-grad">
                        {importing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Import
                    </button>
                </div>
            </div>
        </div>
    );
}

const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)]";
function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6F6A64] mb-1">{label}</label>
            {children}
        </div>
    );
}
